/**
 * #60 — rótulo AS-OF do DADO, único e consistente entre as telas que servem
 * snapshot diário (posição decomposta, exposição, DV01, varejo, fluxo/6m).
 *
 * DOUTRINA:
 * - o horário vem do `computed_at` DO PAYLOAD (quando o dado é), NUNCA do
 *   relógio do browser nem de Date.now() — a frescura é do dado, não da
 *   abertura da tela;
 * - resposta servida LIVE não ganha rótulo de snapshot (seria mentira nova);
 * - caso de borda: meta ausente (payload antigo/erro) → o rótulo DIZ que não
 *   sabe — nunca omite em silêncio nem inventa horário;
 * - discreto: informação de leitura, não alarme.
 */
import type { AsOfMeta } from "../../api/types";

const TOOLTIP =
  "As-of do DADO desta tela (snapshot diário do precompute ou compute ao " +
  "vivo desta consulta) — não é a hora em que a tela foi aberta.";

/** computed_at (UTC-naive ISO) → "dd/mm HH:mm (BRT)" sem tocar no relógio local. */
function fmtBrt(isoUtc: string): string {
  const [datePart, timePart] = isoUtc.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  // UTC -> BRT = -3h, ajuste puramente aritmético sobre o timestamp do dado
  const t = Date.UTC(y, m - 1, d, hh - 3, mm);
  const b = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(b.getUTCDate())}/${p(b.getUTCMonth() + 1)} ${p(b.getUTCHours())}:${p(b.getUTCMinutes())} BRT`;
}

export function AsOfBadge({ asOf }: { asOf?: AsOfMeta | null }) {
  let texto: string;
  if (!asOf) {
    texto = "as-of desconhecido"; // borda: diz que não sabe, nunca inventa
  } else if (asOf.servido === "live") {
    texto = "ao vivo · computado nesta consulta";
  } else if (asOf.computed_at) {
    texto = `dados de ${fmtBrt(asOf.computed_at)} · snapshot diário`;
  } else {
    texto = "snapshot diário · horário desconhecido";
  }
  return (
    <span
      className="text-[10px] text-white/40 whitespace-nowrap cursor-help"
      title={TOOLTIP}
      data-testid="asof-badge"
    >
      {texto}
    </span>
  );
}
